import { forwardRef } from 'react';
import { cn } from './cn';

export type IconButtonVariant = 'ghost' | 'solid' | 'outline';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Required: icon-only buttons have no visible text, so the label must be provided for screen readers. */
  'aria-label': string;
  /** An icon element (import from './icons'). */
  children: React.ReactNode;
}

const base =
  'inline-flex items-center justify-center rounded-xl shrink-0 ' +
  'transition-all duration-200 active:scale-95 select-none ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-canvas ' +
  'disabled:opacity-50 disabled:pointer-events-none';

const variants: Record<IconButtonVariant, string> = {
  ghost: 'bg-transparent text-muted hover:bg-primary/10 hover:text-primary',
  solid: 'bg-primary text-white hover:bg-primary-dark',
  outline:
    'bg-transparent border border-line text-content hover:border-primary/40 hover:text-primary',
};

const sizes: Record<IconButtonSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-11 w-11',
};

/**
 * Icon-only button — replaces ad-hoc MUI <IconButton /> usages.
 * `aria-label` is required since there is no visible text label.
 * variant: ghost (default) | solid | outline.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant = 'ghost', size = 'md', type = 'button', className, children, ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(base, variants[variant], sizes[size], className)}
      {...props}
    >
      {children}
    </button>
  ),
);

IconButton.displayName = 'IconButton';
